import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreateIcon from '@mui/icons-material/Create';
import LinkIcon from '@mui/icons-material/Link';
import { apiFetch } from '../api/client';
import EmailMessageList from '../components/email/EmailMessageList';
import EmailReadingPane from '../components/email/EmailReadingPane';
import EmailComposer from '../components/email/EmailComposer';
import { stripHtml } from '../components/email/emailUtils';

const LABELS = [
  { id: 'INBOX', label: 'Entrada' },
  { id: 'SENT', label: 'Enviados' },
  { id: 'DRAFT', label: 'Rascunhos' },
];

export default function EmailInboxPage() {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [connected, setConnected] = useState(null);
  const [tab, setTab] = useState(0);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [compose, setCompose] = useState(null);
  const folderCache = React.useRef({});

  const labelId = LABELS[tab]?.id || 'INBOX';

  const checkStatus = useCallback(async () => {
    const res = await apiFetch('/api/gmail/status');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao verificar Gmail');
    setConnected(Boolean(data.connected));
    return Boolean(data.connected);
  }, []);

  const connectGmail = async () => {
    setError('');
    const origin = encodeURIComponent(window.location.origin);
    const res = await apiFetch(`/api/auth/google/connect-url?returnTo=/email&origin=${origin}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || 'Não foi possível iniciar conexão Google.');
      return;
    }
    window.location.href = data.url;
  };

  const loadMessages = useCallback(async ({ fresh = false } = {}) => {
    const cached = folderCache.current[labelId];
    if (cached && !fresh) {
      setMessages(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const ok = await checkStatus();
      if (!ok) {
        setMessages([]);
        setSelected(null);
        setDetail(null);
        return;
      }
      const q = fresh ? '&fresh=1' : '';
      const res = await apiFetch(`/api/gmail/messages?label=${labelId}&maxResults=40${q}`);
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setConnected(false);
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Erro ao listar e-mails');
      const list = data.messages || [];
      folderCache.current[labelId] = list;
      setMessages(list);
    } catch (e) {
      if (!cached) setError(e.message || 'Falha ao carregar caixa');
    } finally {
      setLoading(false);
    }
  }, [checkStatus, labelId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_connected') === '1' || params.get('google_error')) {
      if (params.get('google_error')) setError(params.get('google_error'));
      window.history.replaceState({}, '', '/email');
    }
    loadMessages();
  }, [loadMessages]);

  const openMessage = async (msg) => {
    setSelected(msg.id);
    setDetail(null);
    setOpening(true);
    try {
      const res = await apiFetch(`/api/gmail/messages/${encodeURIComponent(msg.id)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao abrir e-mail');
      setDetail(data);
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, unread: false } : m)));
    } catch (e) {
      setError(e.message);
    } finally {
      setOpening(false);
    }
  };

  const trashMessage = async () => {
    if (!selected) return;
    const res = await apiFetch(`/api/gmail/messages/${encodeURIComponent(selected)}/trash`, { method: 'POST' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || 'Erro ao excluir');
      return;
    }
    setSelected(null);
    setDetail(null);
    delete folderCache.current[labelId];
    loadMessages({ fresh: true });
  };

  if (connected === false) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Stack spacing={2} alignItems="flex-start">
          <Typography variant="h5" fontWeight={800}>Caixa de e-mail</Typography>
          <Alert severity="info">
            Conecte o Gmail uma vez para abrir a caixa aqui e enviar com o seu endereço.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          <Button variant="contained" startIcon={<LinkIcon />} onClick={connectGmail}>
            Conectar Gmail com Google
          </Button>
        </Stack>
      </Box>
    );
  }

  const showList = !compact || !selected;
  const showRead = !compact || Boolean(selected);

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, height: 'calc(100vh - 108px)', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <MailOutlineIcon color="primary" />
          <Typography variant="h6" fontWeight={800}>E-mail</Typography>
          <Chip size="small" label="Gmail" variant="outlined" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<CreateIcon />}
            variant="contained"
            onClick={() => setCompose({ mode: 'new', initial: { to: '', subject: '', html: '' } })}
          >
            Novo
          </Button>
          <IconButton onClick={() => loadMessages({ fresh: true })} disabled={loading} aria-label="Atualizar">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelected(null); setDetail(null); }} sx={{ mb: 1, minHeight: 40 }}>
        {LABELS.map((l) => (
          <Tab key={l.id} label={l.label} sx={{ textTransform: 'none', fontWeight: 700 }} />
        ))}
      </Tabs>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: compact ? '1fr' : 'minmax(280px, 0.9fr) 1.3fr',
          gap: 1.5,
        }}
      >
        {showList && (
          <Paper variant="outlined" sx={{ overflow: 'auto', borderRadius: 2, minHeight: 0 }}>
            <EmailMessageList
              messages={messages}
              selectedId={selected}
              loading={loading}
              onSelect={openMessage}
            />
          </Paper>
        )}
        {showRead && (
          <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2, minHeight: 0, display: compact && !selected ? 'none' : 'block' }}>
            <EmailReadingPane
              detail={detail}
              loading={opening}
              showBack={compact}
              onBack={() => { setSelected(null); setDetail(null); }}
              onTrash={trashMessage}
              onReply={() => setCompose({
                mode: 'reply',
                replyId: detail?.id,
                originalText: stripHtml(detail?.html) || detail?.text || '',
                initial: {
                  to: detail?.from || '',
                  subject: (detail?.subject || '').startsWith('Re:') ? detail.subject : `Re: ${detail?.subject || ''}`,
                  html: '',
                },
              })}
            />
          </Paper>
        )}
      </Box>

      {compose && (
        <EmailComposer
          open
          mode={compose.mode}
          replyId={compose.replyId}
          originalText={compose.originalText}
          initial={compose.initial}
          onClose={() => setCompose(null)}
          onSent={() => {
            setCompose(null);
            delete folderCache.current.SENT;
            delete folderCache.current[labelId];
            loadMessages({ fresh: true });
          }}
        />
      )}
    </Box>
  );
}
